/**
 * Moteur de calcul des aides (MaPrimeRénov', CEE, Éco-PTZ, TVA 5,5).
 *
 * Estimations basées sur les barèmes officiels Anah / France Rénov' 2026
 * et les fourchettes de primes CEE Coup de pouce en vigueur. Les chiffres
 * restent indicatifs et doivent être validés par devis RGE pour engagement.
 */

import {
  computeRevenuBracket,
  type AnahZone,
  type RevenuBracket,
} from '../../../lib/zoning';
import { findTravaux } from './travaux-config';
import type {
  AidsFunnelState,
  ProfileType,
} from './useAidsStore';

export interface AidsResults {
  /* MaPrimeRénov' */
  mprMin: number;
  mprMax: number;
  mprLabel: string;
  /* CEE (Certificats d'Économie d'Énergie) */
  ceeMin: number;
  ceeMax: number;
  /* Coup de Pouce : fraction des CEE provenant du bonus Coup de Pouce */
  coupDePouceMin: number;
  coupDePouceMax: number;
  /* Éco-PTZ (prêt, non subvention) */
  ecoPtzMax: number;
  /* TVA 5,5 % (économie vs 20 %) */
  tvaEco: number;
  tvaEcoMax: number;
  /* Coût total moyen TTC du chantier (avant aides) */
  totalCostMin: number;
  totalCostMax: number;
  /* Total des aides cumulées (hors prêt) */
  totalMin: number;
  totalMax: number;
  /* Reste à charge (coût - aides cumulées) */
  resteAChargeMin: number;
  resteAChargeMax: number;
  /* Taux d'aide (% du coût travaux couvert) */
  tauxAideMin: number;
  tauxAideMax: number;
  /* Économie énergie annuelle estimée (€/an) — non null si energyBillEur fourni */
  economieAnnuelleMin: number | null;
  economieAnnuelleMax: number | null;
  /* Retour sur investissement (mois) — non null si economieAnnuelle > 0 */
  roiMonthsMin: number | null;
  roiMonthsMax: number | null;
  /* Progression DPE estimée — non null si dpeActuel fourni */
  dpeActuel: string | null;
  dpeTarget: string | null;
  /* Méta */
  isAmpleur: boolean;
  bracket: RevenuBracket | null;
  warnings: string[];
}

/* ─── Classes DPE ────────────────────────────────────────── */

const DPE_CLASSES = ['G', 'F', 'E', 'D', 'C', 'B', 'A'];

/**
 * Score de progression DPE par type de travail.
 * Exprimé en "nombre de classes DPE gagnées" (approximation).
 */
const DPE_PROGRESS_BY_TRAVAIL: Record<string, number> = {
  'reno-globale': 3,
  combles: 0.8,
  murs: 1.2,
  sol: 0.5,
  fenetres: 0.6,
  pac: 1.5,
  biomasse: 1.0,
  'solaire-thermique': 0.5,
  ceth: 0.4,
  vmc: 0.5,
  audit: 0,
};

/**
 * Estime la progression DPE à partir des travaux sélectionnés.
 * Retourne la nouvelle classe DPE ou null si dpeActuel non fourni.
 */
function estimateDpeTarget(dpeActuel: string | null, travauxIds: string[]): string | null {
  if (!dpeActuel) return null;
  const currentIdx = DPE_CLASSES.indexOf(dpeActuel);
  if (currentIdx < 0) return null;

  let totalProgress = 0;
  for (const id of travauxIds) {
    totalProgress += DPE_PROGRESS_BY_TRAVAIL[id] ?? 0;
  }

  // Chaque classe DPE nécessite ~1 point de progression
  const classesGained = Math.floor(totalProgress);
  const targetIdx = Math.min(DPE_CLASSES.length - 1, currentIdx + classesGained);
  const target = DPE_CLASSES[targetIdx];

  // Pas de changement → même classe (peut arriver si progression < 1)
  return target ?? dpeActuel;
}

/* ─── Économies énergétiques par travail ─────────────────── */

/**
 * Taux d'économie sur la facture énergie globale annuelle par travail.
 * Source : ADEME, ANAH — approximations moyennes pour résidentiel.
 * Fourchette min–max (taux appliqué à la facture annuelle fournie).
 */
const ENERGY_SAVINGS_RATE: Record<string, [number, number]> = {
  'reno-globale': [0.35, 0.55],
  combles: [0.07, 0.15],
  murs: [0.10, 0.20],
  sol: [0.04, 0.09],
  fenetres: [0.04, 0.08],
  pac: [0.15, 0.30],
  biomasse: [0.10, 0.20],
  'solaire-thermique': [0.05, 0.12],
  ceth: [0.03, 0.08],
  vmc: [0.04, 0.08],
  audit: [0, 0],
};

function estimateEnergyEconomy(
  travauxIds: string[],
  annualBill: number,
): { econMin: number; econMax: number } {
  // Les taux sont partiellement cumulatifs mais avec diminution marginale.
  // On accumule mais on plafonne à 75 % de la facture totale.
  let rateMin = 0;
  let rateMax = 0;
  for (const id of travauxIds) {
    const rate = ENERGY_SAVINGS_RATE[id];
    if (rate) {
      rateMin += rate[0];
      rateMax += rate[1];
    }
  }
  rateMin = Math.min(0.75, rateMin);
  rateMax = Math.min(0.75, rateMax);

  return {
    econMin: Math.round(annualBill * rateMin),
    econMax: Math.round(annualBill * rateMax),
  };
}

/* ─── Barèmes MaPrimeRénov' 2026 ─────────────────────────── */

/** Taux MaPrimeRénov' propriétaire occupant par tranche de revenus. */
const MPR_TAUX: Record<RevenuBracket, number> = {
  'tres-modestes': 0.7,
  modestes: 0.6,
  intermediaires: 0.45,
  superieurs: 0.3,
};

/** Plafonds d'aide MPR (Décarbonation et Ampleur) par tranche. */
const MPR_CAPS: Record<RevenuBracket, { decarb: number; ampleur: number }> = {
  'tres-modestes': { decarb: 11_000, ampleur: 63_000 },
  modestes: { decarb: 9_000, ampleur: 54_600 },
  intermediaires: { decarb: 6_750, ampleur: 40_950 },
  superieurs: { decarb: 4_500, ampleur: 27_300 },
};

/* ─── Moteur ─────────────────────────────────────────────── */

const EMPTY: AidsResults = {
  mprMin: 0,
  mprMax: 0,
  mprLabel: "MaPrimeRénov'",
  ceeMin: 0,
  ceeMax: 0,
  coupDePouceMin: 0,
  coupDePouceMax: 0,
  ecoPtzMax: 0,
  tvaEco: 0,
  tvaEcoMax: 0,
  totalCostMin: 0,
  totalCostMax: 0,
  totalMin: 0,
  totalMax: 0,
  resteAChargeMin: 0,
  resteAChargeMax: 0,
  tauxAideMin: 0,
  tauxAideMax: 0,
  economieAnnuelleMin: null,
  economieAnnuelleMax: null,
  roiMonthsMin: null,
  roiMonthsMax: null,
  dpeActuel: null,
  dpeTarget: null,
  isAmpleur: false,
  bracket: null,
  warnings: [],
};

export function computeAids(
  form: AidsFunnelState,
  anahZone: AnahZone,
): AidsResults {
  if (!form.profile || form.travaux.length === 0) return { ...EMPTY };

  const bracket: RevenuBracket | null =
    form.rfr != null && form.householdSize != null
      ? computeRevenuBracket(form.rfr, form.householdSize, anahZone)
      : null;

  let mprMin = 0;
  let mprMax = 0;
  let ceeMin = 0;
  let ceeMax = 0;
  let coupDePouceMin = 0;
  let coupDePouceMax = 0;
  let totalCostMin = 0;
  let totalCostMax = 0;

  const hasRenoGlobale = form.travaux.includes('reno-globale');
  const distinctGestes = new Set(
    form.travaux
      .map((id) => findTravaux(id))
      .filter((t): t is NonNullable<ReturnType<typeof findTravaux>> => !!t)
      .filter((t) => t.category !== 'audit' && t.category !== 'globale')
      .map((t) => t.id),
  );
  const isAmpleur = hasRenoGlobale || distinctGestes.size >= 3;

  for (const id of form.travaux) {
    const t = findTravaux(id);
    if (!t) continue;
    ceeMin += t.ceeMin;
    ceeMax += t.ceeMax;
    totalCostMin += t.costMin;
    totalCostMax += t.costMax;
    // Coup de Pouce = travaux avec tag 'coup-pouce'
    if (t.tags.includes('coup-pouce')) {
      // Majoration CEE ×1.5 pour Coup de Pouce
      coupDePouceMin += Math.round(t.ceeMin * 0.5);
      coupDePouceMax += Math.round(t.ceeMax * 0.5);
    }
  }

  let mprLabel = "MaPrimeRénov'";
  let ecoPtzMax = 0;
  const warnings: string[] = [];

  const applyMprByGestes = (taux: number) => {
    for (const id of form.travaux) {
      const t = findTravaux(id);
      if (!t?.eligibleMPR) continue;
      mprMin += Math.round(t.costMin * taux);
      mprMax += Math.round(t.costMax * taux);
    }
  };

  switch (form.profile satisfies ProfileType) {
    case 'occupant': {
      if (!bracket) {
        return {
          ...EMPTY,
          ceeMin,
          ceeMax,
          coupDePouceMin,
          coupDePouceMax,
          totalCostMin,
          totalCostMax,
          resteAChargeMin: totalCostMin - ceeMin,
          resteAChargeMax: totalCostMax - ceeMax,
          tauxAideMin: totalCostMin > 0 ? Math.round((ceeMin / totalCostMin) * 100) : 0,
          tauxAideMax: totalCostMax > 0 ? Math.round((ceeMax / totalCostMax) * 100) : 0,
          dpeActuel: form.dpeActuel,
          dpeTarget: estimateDpeTarget(form.dpeActuel, form.travaux),
          isAmpleur,
        };
      }
      const taux = MPR_TAUX[bracket];
      applyMprByGestes(taux);
      const cap = MPR_CAPS[bracket][isAmpleur ? 'ampleur' : 'decarb'];
      mprMax = Math.min(mprMax, cap);
      mprMin = Math.min(mprMin, Math.round(cap * 0.6));
      mprLabel = `MaPrimeRénov'${isAmpleur ? ' Parcours accompagné' : ' Décarbonation'}`;
      {
        const gestes = distinctGestes.size;
        ecoPtzMax = hasRenoGlobale
          ? 50_000
          : gestes >= 3
            ? 30_000
            : gestes === 2
              ? 25_000
              : gestes === 1 && form.travaux.includes('fenetres')
                ? 7_000
                : 15_000;
      }
      break;
    }
    case 'bailleur': {
      if (!bracket) {
        return {
          ...EMPTY,
          ceeMin,
          ceeMax,
          coupDePouceMin,
          coupDePouceMax,
          totalCostMin,
          totalCostMax,
          resteAChargeMin: totalCostMin - ceeMin,
          resteAChargeMax: totalCostMax - ceeMax,
          tauxAideMin: totalCostMin > 0 ? Math.round((ceeMin / totalCostMin) * 100) : 0,
          tauxAideMax: totalCostMax > 0 ? Math.round((ceeMax / totalCostMax) * 100) : 0,
          dpeActuel: form.dpeActuel,
          dpeTarget: estimateDpeTarget(form.dpeActuel, form.travaux),
          isAmpleur,
        };
      }
      const taux = MPR_TAUX[bracket] * 0.7;
      applyMprByGestes(taux);
      mprMin = Math.min(mprMin, 12_000);
      mprMax = Math.min(mprMax, 20_000);
      mprLabel = "MaPrimeRénov' Bailleur";
      ecoPtzMax = 30_000;
      warnings.push(
        'Engagement de location en résidence principale pendant 6 ans après les travaux.',
      );
      break;
    }
    case 'copropriete': {
      const baseMin = 15_000;
      const baseMax = 35_000;
      const factor =
        form.travaux.length >= 3 ? 1 : form.travaux.length === 2 ? 0.75 : 0.45;
      mprMin = Math.round(baseMin * factor);
      mprMax = Math.round(baseMax * factor);
      ceeMin = Math.round(ceeMin * 1.5);
      ceeMax = Math.round(ceeMax * 1.5);
      coupDePouceMin = Math.round(coupDePouceMin * 1.5);
      coupDePouceMax = Math.round(coupDePouceMax * 1.5);
      mprLabel = "MaPrimeRénov' Copropriétés";
      ecoPtzMax = 50_000;
      warnings.push(
        "Vote en assemblée générale requis. Audit énergétique préalable obligatoire pour MPR Copro.",
      );
      break;
    }
    case 'locataire': {
      warnings.push(
        "Accord écrit du propriétaire requis avant tout dépôt de dossier. MaPrimeRénov' et éco-PTZ ne sont pas accessibles au locataire.",
      );
      break;
    }
  }

  // Économie TVA
  const TVA_DIFF = (0.2 - 0.055) / 1.055;
  const tvaEco = Math.round(totalCostMin * TVA_DIFF);
  const tvaEcoMax = Math.round(totalCostMax * TVA_DIFF);

  const totalMin = mprMin + ceeMin + tvaEco;
  const totalMax = mprMax + ceeMax + tvaEcoMax;

  const resteAChargeMin = Math.max(0, totalCostMin - totalMax);
  const resteAChargeMax = Math.max(0, totalCostMax - totalMin);
  const tauxAideMin = totalCostMax > 0 ? Math.round((totalMin / totalCostMax) * 100) : 0;
  const tauxAideMax = totalCostMin > 0 ? Math.round((totalMax / totalCostMin) * 100) : 0;

  // Économies énergie + ROI
  let economieAnnuelleMin: number | null = null;
  let economieAnnuelleMax: number | null = null;
  let roiMonthsMin: number | null = null;
  let roiMonthsMax: number | null = null;

  if (form.energyBillEur && form.energyBillEur > 0) {
    const { econMin, econMax } = estimateEnergyEconomy(form.travaux, form.energyBillEur);
    economieAnnuelleMin = econMin;
    economieAnnuelleMax = econMax;
    if (econMax > 0 && resteAChargeMax > 0) {
      roiMonthsMin = Math.round((resteAChargeMin / econMax) * 12);
      roiMonthsMax = Math.round((resteAChargeMax / econMin) * 12);
    }
  }

  // Progression DPE
  const dpeActuel = form.dpeActuel ?? null;
  const dpeTarget = estimateDpeTarget(dpeActuel, form.travaux);

  return {
    mprMin,
    mprMax,
    mprLabel,
    ceeMin,
    ceeMax,
    coupDePouceMin,
    coupDePouceMax,
    ecoPtzMax,
    tvaEco,
    tvaEcoMax,
    totalCostMin,
    totalCostMax,
    totalMin,
    totalMax,
    resteAChargeMin,
    resteAChargeMax,
    tauxAideMin,
    tauxAideMax,
    economieAnnuelleMin,
    economieAnnuelleMax,
    roiMonthsMin,
    roiMonthsMax,
    dpeActuel,
    dpeTarget,
    isAmpleur,
    bracket,
    warnings,
  };
}

/* ─── Format helper ──────────────────────────────────────── */

export function fmtEuro(n: number): string {
  return n.toLocaleString('fr-FR');
}
